import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

interface ShortcutConfig {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  requiresAuth?: boolean;
}

export const useKeyboardShortcuts = (
  onSearchOpen?: () => void,
  onShortcutsOpen?: () => void
) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const shortcuts: ShortcutConfig[] = [
      // Search
      {
        key: "k",
        ctrlKey: true,
        metaKey: true,
        action: () => {
          if (onSearchOpen && user) {
            onSearchOpen();
          }
        },
        requiresAuth: true,
      },
      // Dashboard
      {
        key: "d",
        ctrlKey: true,
        metaKey: true,
        action: () => navigate("/dashboard"),
        requiresAuth: true,
      },
      // Projects
      {
        key: "p",
        ctrlKey: true,
        metaKey: true,
        action: () => navigate("/collaborations"),
        requiresAuth: true,
      },
      // Messages
      {
        key: "m",
        ctrlKey: true,
        metaKey: true,
        action: () => navigate("/messages"),
        requiresAuth: true,
      },
      // Files
      {
        key: "f",
        ctrlKey: true,
        metaKey: true,
        action: () => navigate("/files"),
        requiresAuth: true,
      },
      // Community Hub
      {
        key: "h",
        ctrlKey: true,
        metaKey: true,
        action: () => navigate("/hub"),
        requiresAuth: false,
      },
      // Home (/) and Help (Shift + /)
      {
        key: "/",
        action: () => navigate("/"),
        requiresAuth: false,
      },
    ];

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs, textareas, or contenteditable elements
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        // Exception: Allow Cmd/Ctrl+K for search even in inputs
        if (
          event.key.toLowerCase() === "k" &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault();
          if (onSearchOpen && user) {
            onSearchOpen();
          }
        }
        return;
      }

      // Special handling for ? (Shift + /)
      if (event.key === "?" && event.shiftKey) {
        event.preventDefault();
        if (onShortcutsOpen) {
          onShortcutsOpen();
        }
        return;
      }

      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const modifierMatch =
          (shortcut.ctrlKey && event.ctrlKey) ||
          (shortcut.metaKey && event.metaKey) ||
          (!shortcut.ctrlKey && !shortcut.metaKey);

        if (keyMatch && modifierMatch) {
          // Skip / if Shift is pressed (that's ?)
          if (shortcut.key === "/" && event.shiftKey) {
            continue;
          }

          // Check if shortcut requires authentication
          if (shortcut.requiresAuth && !user) {
            continue;
          }

          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate, user, onSearchOpen, onShortcutsOpen]);
};
